import { ChangePasswordRequestSchema, PASSWORD_MIN_LENGTH } from "@pokifolio/contracts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { type SubmitEvent, useState } from "react";

import { useApi } from "../../app/api-context";
import { ErrorAlert } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import { Field } from "../../components/ui/field";
import { Input } from "../../components/ui/input";
import { serverFieldError } from "../../lib/errors";
import { type FieldErrors, validate } from "../../lib/forms";
import { AuthLayout } from "./auth-layout";
import { ME_QUERY_KEY, useCurrentUser } from "./session";

/** Field-level server errors are shown next to the field, so the general alert is not needed. */
const hasFieldError = (error: unknown): boolean =>
  serverFieldError(error, "currentPassword") !== undefined ||
  serverFieldError(error, "newPassword") !== undefined;

export function ChangePasswordPage() {
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<FieldErrors>({});

  const change = useMutation({
    mutationFn: api.changePassword,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
      await navigate({ to: "/" });
    },
  });

  function onSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    const result = validate(ChangePasswordRequestSchema, { currentPassword, newPassword });
    const mismatch: Record<string, string> =
      newPassword === confirmPassword ? {} : { confirmPassword: "Passwords do not match" };
    if (!result.ok || Object.keys(mismatch).length > 0) {
      setErrors({ ...(result.ok ? {} : result.errors), ...mismatch });
      return;
    }
    setErrors({});
    change.mutate(result.data);
  }

  const forced = user.mustChangePassword;
  return (
    <AuthLayout
      title={forced ? "Choose a new password" : "Change your password"}
      description={
        forced
          ? "Your account uses a temporary password. Choose your own to continue."
          : "Other devices will be signed out."
      }
    >
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <Field
          id="currentPassword"
          label={forced ? "Temporary password" : "Current password"}
          error={errors.currentPassword ?? serverFieldError(change.error, "currentPassword")}
        >
          <Input
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
        </Field>
        <Field
          id="newPassword"
          label="New password"
          hint={`At least ${PASSWORD_MIN_LENGTH} characters.`}
          error={errors.newPassword ?? serverFieldError(change.error, "newPassword")}
        >
          <Input
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </Field>
        <Field id="confirmPassword" label="Repeat new password" error={errors.confirmPassword}>
          <Input
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </Field>
        {change.isError && !hasFieldError(change.error) && <ErrorAlert error={change.error} />}
        <Button type="submit" disabled={change.isPending}>
          {change.isPending ? "Saving…" : "Save new password"}
        </Button>
        {!forced && (
          <Link to="/" className="text-center text-sm text-primary hover:underline">
            Cancel
          </Link>
        )}
      </form>
    </AuthLayout>
  );
}
